import React, { useState, useEffect, useRef } from 'react';
import { subscribeToTestSchedule } from '../services/reviewsService';
import LoadingSpinner from './LoadingSpinner';
import './TestScheduleScreen.css';

const TestScheduleScreen = ({ educationLevel, grade, onBack }) => {
  const [testScheduleFile, setTestScheduleFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const unsubscribeRef = useRef(null);

  // الاستماع لتحديثات جدول الاختبارات في الوقت الفعلي
  useEffect(() => {
    // إلغاء الاشتراك السابق إذا كان موجوداً
    if (unsubscribeRef.current) {
      unsubscribeRef.current();
      unsubscribeRef.current = null;
    }

    // إذا لم يتم اختيار المرحلة والصف، لا نستمع
    if (!educationLevel || !grade) {
      setTestScheduleFile(null);
      setLoading(false);
      return;
    }

    setLoading(true);
    
    // الاشتراك في التحديثات الفورية
    const unsubscribe = subscribeToTestSchedule(
      educationLevel,
      grade,
      (testSchedule) => {
        setLoading(false);
        if (testSchedule) {
          setTestScheduleFile({
            url: testSchedule.fileUrl,
            name: testSchedule.fileName
          });
        } else {
          setTestScheduleFile(null);
        }
      }
    );

    unsubscribeRef.current = unsubscribe;

    // تنظيف عند unmount أو تغيير المرحلة/الصف
    return () => {
      if (unsubscribeRef.current) {
        unsubscribeRef.current();
        unsubscribeRef.current = null;
      }
    };
  }, [educationLevel, grade]);

  const handleDownload = () => {
    if (!testScheduleFile) {
      return;
    }

    try {
      // إضافة timestamp لتجنب cache
      const separator = testScheduleFile.url.includes('?') ? '&' : '?';
      const downloadUrl = `${testScheduleFile.url}${separator}t=${Date.now()}`;
      
      // محاولة استخدام download attribute أولاً
      try {
        const link = document.createElement('a');
        link.href = downloadUrl;
        link.download = testScheduleFile.name || 'جدول_الاختبارات.jpg';
        link.target = '_blank';
        link.rel = 'noopener noreferrer';
        link.style.display = 'none';
        
        document.body.appendChild(link);
        link.click();
        
        setTimeout(() => {
          if (document.body.contains(link)) {
            document.body.removeChild(link);
          }
        }, 100);
      } catch (linkError) {
        // Fallback: فتح الرابط في نافذة جديدة
        console.warn('استخدام fallback للتحميل:', linkError);
        window.open(downloadUrl, '_blank', 'noopener,noreferrer');
      }
    } catch (error) {
      console.error('خطأ في تحميل الملف:', error);
    }
  };

  // دالة للتحقق من أن الملف صورة
  const isImageFile = (fileName) => {
    const extension = fileName.split('.').pop()?.toLowerCase();
    return ['jpg', 'jpeg', 'png'].includes(extension);
  };

  const getLevelLabel = () => {
    return educationLevel === 'middle' ? 'المرحلة المتوسطة' : 'المرحلة الثانوية';
  };

  const getGradeLabel = (gradeNum) => {
    const labels = { 1: 'الأول', 2: 'الثاني', 3: 'الثالث' };
    return labels[gradeNum] || `صف ${gradeNum}`;
  };

  // شاشة عرض الملف
  if (educationLevel && grade) {
    return (
      <div className="test-schedule-screen">
        <div className="test-schedule-container" style={{ position: 'relative' }}>
          <div className="selection-header">
            <button className="back-button ripple-effect magnetic-button" onClick={onBack}>
              ← رجوع
            </button>
            <h1 className="selection-title">جدول الاختبارات</h1>
          </div>

          <div className="test-schedule-content">
            <div className="selection-info">
              <div className="info-item">
                <span className="info-value">{getLevelLabel()}</span>
              </div>
              <div className="info-item">
                <span className="info-value">الصف {getGradeLabel(grade)}</span>
              </div>
            </div>

            {loading ? (
              <LoadingSpinner message="جاري تحميل جدول الاختبار..." />
            ) : testScheduleFile ? (
              <div className="file-preview-container">
                {/* عرض الصورة مباشرة إذا كان الملف صورة */}
                {isImageFile(testScheduleFile.name) ? (
                  <div className="image-preview-wrapper">
                      <img 
                        src={testScheduleFile.url} 
                      alt="جدول الاختبارات"
                        onError={(e) => {
                          console.error('خطأ في تحميل الصورة:', e);
                          e.target.style.display = 'none';
                        }}
                      />
                  </div>
                ) : (
                  <div className="file-icon-wrapper">
                    <div className="file-icon-large">📄</div>
                  </div>
                )}
                {/* زر التحميل فقط */}
                <div className="file-content-wrapper download-button-wrapper">
                  <button 
                    className="action-button download-button ripple-effect magnetic-button pulse-element" 
                    onClick={handleDownload}
                  >
                      <span className="button-icon">⬇️</span>
                    <span className="button-text">تحميل جدول الاختبارات</span>
                    </button>
                </div>
              </div>
            ) : (
              <div className="no-file-container">
                <div className="no-file-icon">📭</div>
                <h3 className="no-file-title">لا يوجد جدول اختبار</h3>
                <p className="no-file-message">لا يوجد جدول اختبار متاح لهذا الصف حالياً</p>
                <p className="no-file-hint">يرجى التحقق من أن المدير قام برفع جدول الاختبار</p>
              </div>
            )}
          </div>

          <div className="test-schedule-footer">
            <p>مدير المدرسة: محمد نصر الدين</p>
          </div>
        </div>
      </div>
    );
  }

  // إذا لم يتم تمرير المرحلة والصف، نعرض رسالة خطأ
  return (
    <div className="test-schedule-screen">
      <div className="test-schedule-container" style={{ position: 'relative' }}>
        <div className="selection-header">
          {onBack && (
            <button className="back-button ripple-effect magnetic-button" onClick={onBack}>
              ← رجوع
            </button>
          )}
          <h1 className="selection-title">جدول الاختبارات</h1>
        </div>
        <div className="error-container">
          <div className="error-icon">⚠️</div>
          <p className="error-message">لم يتم اختيار المرحلة والصف</p>
        </div>
      </div>
    </div>
  );
};

export default TestScheduleScreen;

